import React, { type FunctionComponent, type ComponentType, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import {
	type MenuListComponentProps,
	type OptionProps,
	type ControlProps,
	components,
} from '@atlaskit/select';

import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import GenericError from '../../assets/GenericError.svg';
import messages from './messages';
import { type OptionType, getDesignTokenBackgroundColor } from './utils';

const CustomOption: FunctionComponent<OptionProps<OptionType>> = (props) => {
	const { label, iconUrl, placeholderColor } = props.data;

	return (
		<components.Option {...props}>
			<OptionWrapper>
				{iconUrl ? (
					<Icon src={iconUrl} alt="" />
				) : (
					placeholderColor && (
						<PlaceholderIcon
							placeholderColor={placeholderColor}
							data-testId="project-create-importer-upload-flow.common.ui.custom-select-components.placeholder-icon"
						>
							{label?.charAt(0)}
						</PlaceholderIcon>
					)
				)}
				<TextWrapper>{label}</TextWrapper>
			</OptionWrapper>
		</components.Option>
	);
};

const CustomControl: FunctionComponent<ControlProps<OptionType>> = ({ children, ...props }) => {
	const selectedOption = props.getValue();
	const iconUrl = selectedOption && selectedOption[0] != null ? selectedOption[0].iconUrl : null;
	const placeholderColor =
		selectedOption && selectedOption[0] != null ? selectedOption[0].placeholderColor : null;
	return (
		<components.Control {...props}>
			<ControlWrapper>
				{iconUrl && <Icon src={iconUrl} alt="" />}
				{!iconUrl && !!placeholderColor && (
					<PlaceholderIconControl
						placeholderColor={placeholderColor}
						data-testId="project-create-importer-upload-flow.common.ui.custom-select-components.placeholder-icon-control"
					>
						{selectedOption?.[0]?.label?.charAt(0)}
					</PlaceholderIconControl>
				)}
				{children}
			</ControlWrapper>
		</components.Control>
	);
};

interface CustomProps {
	selectProps: {
		menuListProps: {
			hasSearchErrorOccurred: boolean;
			searchErrorOccurredDescription: string;
			isLoadingMore: boolean;
			hasLoadingMoreErrorOccurred: boolean;
			loadingMoreErrorOccurredDescription: string;
			onRetryLoadingMore: () => void;
		};
	};
}

const CustomMenuList: FunctionComponent<MenuListComponentProps<OptionType> & CustomProps> = (
	props,
) => {
	const { formatMessage } = useIntl();
	const {
		selectProps: {
			menuListProps: {
				hasSearchErrorOccurred,
				searchErrorOccurredDescription,
				isLoadingMore,
				hasLoadingMoreErrorOccurred,
				loadingMoreErrorOccurredDescription,
				onRetryLoadingMore,
			},
		},
	} = props;

	const content = useMemo(() => {
		if (hasSearchErrorOccurred) {
			return (
				<ErrorWrapper>
					<SearchErrorIcon src={GenericError} />
					<ErrorContent>{searchErrorOccurredDescription}</ErrorContent>
				</ErrorWrapper>
			);
		}
		if (hasLoadingMoreErrorOccurred) {
			return (
				<ErrorWrapper>
					<ErrorIcon label="" size="xlarge" />
					<ErrorContent>{loadingMoreErrorOccurredDescription}</ErrorContent>
					<Button onClick={onRetryLoadingMore} appearance="link" spacing="none">
						{formatMessage(messages.tryAgain)}
					</Button>
				</ErrorWrapper>
			);
		}
		return (
			<>
				{props.children}
				{isLoadingMore && <LoadingWrapper>{formatMessage(messages.loadMore)}</LoadingWrapper>}
			</>
		);
	}, [
		formatMessage,
		hasLoadingMoreErrorOccurred,
		hasSearchErrorOccurred,
		isLoadingMore,
		loadingMoreErrorOccurredDescription,
		onRetryLoadingMore,
		props.children,
		searchErrorOccurredDescription,
	]);

	return <components.MenuList {...props}>{content}</components.MenuList>;
};

export const customComponents = {
	Option: CustomOption,
	Control: CustomControl,
};

export const customComponentsWithMenuList = {
	...customComponents,
	// using ComponentType<any> to allow custom props to be passed to menu list
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
	MenuList: CustomMenuList as ComponentType<any>,
};

interface PlaceholderIconProps {
	placeholderColor: string;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionWrapper = styled.div({
	display: 'flex',
	padding: token('space.050', '4px'),
	alignItems: 'center',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> img': {
		margin: `0 ${token('space.100', '8px')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ControlWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	width: '100%',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> img': {
		marginLeft: token('space.100', '8px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextWrapper = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	width: token('space.300', '24px'),
	height: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchErrorIcon = styled.img({
	width: token('space.500', '40px'),
	height: token('space.500', '40px'),
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PlaceholderIcon = styled.div<PlaceholderIconProps>({
	width: '24px',
	height: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: `${({ placeholderColor }: PlaceholderIconProps) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		getDesignTokenBackgroundColor(placeholderColor)}`,
	margin: `0 ${token('space.100', '8px')}`,
	lineHeight: '24px',
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PlaceholderIconControl = styled(PlaceholderIcon)<PlaceholderIconProps>({
	marginRight: '0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorContent = styled.span({
	textAlign: 'center',
	lineHeight: '20px',
	fontSize: '14px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingWrapper = styled.span({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: token('space.300', '24px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N400),
	lineHeight: '24px',
});
